/**
 * isNetworkError - True when `error` is fetch's network-level failure
 * (offline, blocked request, DNS/socket failure) — the retryable class,
 * as opposed to an HTTP error or a JSON parse failure. The message list
 * covers Chrome ("Failed to fetch"), Safari ("Load failed"), and React
 * Native ("Network request failed").
 *
 * @param {unknown} error - The caught error
 * @returns {boolean}
 */
export function isNetworkError(error) {
  return (
    error?.name === 'TypeError' &&
    (error?.message?.includes('Load failed') ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('Network request failed'))
  );
}

/**
 * safeFetch - A wrapper around fetch that handles network errors gracefully
 *
 * This utility was created to address Sentry issue CHARITYSTACK-MONOREPO-44
 * where "TypeError: Load failed" errors were being thrown as unhandled rejections
 * on mobile Safari when network conditions are poor.
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @param {object} config - Configuration for error handling
 * @param {string} config.context - Description of what's being fetched (for logging)
 * @param {boolean} config.silent - If true, don't log errors to console (default: false)
 * @param {number} config.retries - Number of retries on network failure (default: 1)
 * @param {number} config.retryDelay - Delay between retries in ms (default: 1000)
 * @param {'fixed'|'exponential'} config.backoff - Delay strategy (default: 'fixed')
 * @param {boolean} config.retryOn5xx - Also retry on 5xx server errors (default: false)
 * @returns {Promise<{ok: boolean, data: any, error: string|null}>}
 */
export async function safeFetch(url, options = {}, config = {}) {
  const {
    context = 'API call',
    silent = false,
    retries = 1,
    retryDelay = 1000,
    backoff = 'fixed',
    retryOn5xx = false,
  } = config;

  let lastError = null;

  /** Calculate delay for this attempt based on backoff strategy */
  const getDelay = (attempt) =>
    backoff === 'exponential' ? retryDelay * Math.pow(2, attempt) : retryDelay;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);

      // Handle HTTP errors (4xx, 5xx)
      if (!response.ok) {
        // Retry on 5xx if configured
        if (retryOn5xx && response.status >= 500 && attempt < retries) {
          const delay = getDelay(attempt);
          if (!silent) {
            console.warn(`[safeFetch] ${context} - Server error ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        const errorText = await response.text().catch(() => 'Unknown error');
        return {
          ok: false,
          data: null,
          error: `HTTP ${response.status}: ${errorText}`,
          status: response.status,
          retryAfter: response.headers.get('Retry-After') || '',
          isNetworkError: false,
        };
      }

      // Parse JSON response
      const data = await response.json();
      return {
        ok: true,
        data,
        error: null,
        status: response.status,
        retryAfter: '',
        isNetworkError: false,
      };

    } catch (error) {
      lastError = error;

      // Check if this is a network error (retryable)
      const networkError = isNetworkError(error);

      // Retry on network errors
      if (networkError && attempt < retries) {
        const delay = getDelay(attempt);
        if (!silent) {
          console.warn(`[safeFetch] ${context} - Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Log the error (unless silent mode)
      if (!silent) {
        console.error(`[safeFetch] ${context} failed:`, {
          url,
          error: error.message,
          isNetworkError: networkError,
          attempt: attempt + 1
        });
      }

      // Only retry network errors; break out immediately for all other error types
      if (!networkError) {
        break;
      }
    }
  }

  // All retries exhausted - determine actual error type
  const finalIsNetworkError = isNetworkError(lastError);

  return {
    ok: false,
    data: null,
    error: lastError?.message || 'Unknown error',
    status: null,
    retryAfter: '',
    isNetworkError: finalIsNetworkError,
  };
}

/**
 * safeFetchJSON - Convenience wrapper for JSON API calls
 * Automatically sets Content-Type header and handles JSON parsing
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {object} config - safeFetch config
 * @returns {Promise<{ok: boolean, data: any, error: string|null}>}
 */
export async function safeFetchJSON(url, options = {}, config = {}) {
  const jsonOptions = {
    ...options,
    headers: {
      'Accept': '*/*',
      'Content-Type': 'application/json',
      ...options.headers
    }
  };

  return safeFetch(url, jsonOptions, config);
}

export default safeFetch;
